import { api } from '@/lib/api/client';
import type {
  LeoEntry,
  LeoFilters,
  LeoListResponse,
  CreateLeoDto,
  UpdateLeoDto,
  MergeLeoDto,
  DuplicateCheckResult,
  LeoType,
} from '../types';

export const leoKeys = {
  all: ['leo'] as const,
  lists: () => [...leoKeys.all, 'list'] as const,
  list: (filters: LeoFilters) => [...leoKeys.lists(), filters] as const,
  detail: (id: string | number) => [...leoKeys.all, 'detail', id] as const,
};

function buildListParams(filters: LeoFilters): Record<string, unknown> {
  const limit = filters.limit ?? 20;
  const params: Record<string, unknown> = {
    limit,
    offset: (filters.page ?? 0) * limit,
  };
  if (filters.s && filters.s.length >= 2) params['s'] = filters.s;
  if (filters.type) params['type'] = filters.type;
  if (filters.status) params['status'] = filters.status;
  if (filters.includeArchived) params['includeArchived'] = true;
  return params;
}

export const leoApi = {
  getEntries: async (filters: LeoFilters): Promise<LeoListResponse> => {
    const response = await api.get<LeoListResponse>('/leo', {
      params: buildListParams(filters),
    });
    return response;
  },

  getEntry: async (id: string | number): Promise<LeoEntry> => {
    const response = await api.get<LeoEntry>(`/leo/${id}`);
    return response;
  },

  // Fuzzy duplicate pre-check (<80% ok, 80–94% warning, ≥95% + same city block)
  checkDuplicate: async (params: {
    name: string;
    city: string;
    type: LeoType;
    excludeId?: number;
  }): Promise<DuplicateCheckResult> => {
    const response = await api.get<DuplicateCheckResult>('/leo/check-duplicate', {
      params,
    });
    return response;
  },

  createEntry: async (data: CreateLeoDto): Promise<LeoEntry> => {
    const response = await api.post<LeoEntry>('/leo', data);
    return response;
  },

  updateEntry: async (
    id: string | number,
    data: UpdateLeoDto,
  ): Promise<LeoEntry> => {
    const response = await api.patch<LeoEntry>(`/leo/${id}`, data);
    return response;
  },

  // Curator: DRAFT → VERIFIED
  verifyEntry: async (id: string | number): Promise<LeoEntry> => {
    const response = await api.post<LeoEntry>(`/leo/${id}/verify`, {});
    return response;
  },

  // Curator merge: source soft-deleted, target survives, merge logged
  mergeEntries: async (data: MergeLeoDto): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>('/leo/merge', data);
    return response;
  },

  // Soft-delete (archive) — references stay readable
  archiveEntry: async (id: string | number): Promise<void> => {
    await api.delete(`/leo/${id}`);
  },
};
